import { useMemo, useState } from 'react';
import { deleteDoc, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type {
  CompanyUpdate,
  FounderUpdateExtraction,
  FounderUpdateInboxItem,
  InvestmentCompany,
  MonthlyUpdate,
  UpdateSentiment,
} from '../types';
import { db, functions } from '../lib/firebase';
import { COLLECTION_FOUNDER_UPDATE_INBOX_ITEMS, ownedItemDocId } from '../lib/firestorePaths';
import { GlobalButton } from './GlobalButton';
import GlobalEmptyState from './GlobalEmptyState';
import { GlobalDropdown } from './GlobalDropdown';
import { formatDateLong } from '../utils/formatters';

type MonthlyUpdateKpiPatch = {
  periodMonth: string;
  revenue?: number;
  burn?: number;
  remainingCash?: number;
  reportSentiment?: number;
  rawText?: string;
};

type CompanyUpdateTimelineProps = {
  companyId: string;
  companyName: string;
  updates: CompanyUpdate[];
  companies: InvestmentCompany[];
  unmatchedInboxItems: FounderUpdateInboxItem[];
  userId: string;
  setUpdates: React.Dispatch<React.SetStateAction<CompanyUpdate[]>>;
  setMonthlyUpdates: React.Dispatch<React.SetStateAction<MonthlyUpdate[]>>;
  onAddManualUpdate?: () => void;
};

function sentimentClassName(sentiment: UpdateSentiment): string {
  return `sentiment sentiment-${sentiment}`;
}

function extractionSentimentToReportSentiment(sentiment: UpdateSentiment): number {
  switch (sentiment) {
    case 'positive':
      return 5;
    case 'mixed':
      return 4;
    case 'neutral':
      return 3;
    case 'negative':
      return 2;
    default:
      return 3;
  }
}

function periodMonthToStoredFormat(periodMonth: string): string {
  return `${periodMonth.slice(0, 7)}-01`;
}

function monthlyUpdateIdForPeriod(companyId: string, periodMonth: string): string {
  return `${companyId}-${periodMonth.slice(0, 7)}`;
}

function hasDisclosedKpi(kpis: FounderUpdateExtraction['kpis']): boolean {
  return (
    kpis.revenue !== undefined ||
    kpis.burn !== undefined ||
    kpis.remainingCash !== undefined
  );
}

function mapExtractionToMonthlyUpdatePatch(
  extraction: FounderUpdateExtraction,
  rawText: string
): MonthlyUpdateKpiPatch | null {
  if (!extraction.periodMonth || !hasDisclosedKpi(extraction.kpis)) {
    return null;
  }

  const patch: MonthlyUpdateKpiPatch = {
    periodMonth: periodMonthToStoredFormat(extraction.periodMonth),
    reportSentiment: extractionSentimentToReportSentiment(extraction.sentiment),
  };

  if (extraction.kpis.revenue !== undefined) {
    patch.revenue = extraction.kpis.revenue;
  }
  if (extraction.kpis.burn !== undefined) {
    patch.burn = extraction.kpis.burn;
  }
  if (extraction.kpis.remainingCash !== undefined) {
    patch.remainingCash = extraction.kpis.remainingCash;
  }

  const trimmed = rawText.trim();
  if (trimmed) {
    patch.rawText = trimmed;
  }

  return patch;
}

function isEmptyField(value: unknown): boolean {
  return value === undefined || value === null;
}

function mergeMonthlyUpdateFromExtraction(
  existing: MonthlyUpdate | undefined,
  patch: MonthlyUpdateKpiPatch
): Partial<MonthlyUpdate> {
  const merged: Partial<MonthlyUpdate> = {};

  if (isEmptyField(existing?.periodMonth)) {
    merged.periodMonth = patch.periodMonth;
  }
  if (isEmptyField(existing?.revenue) && patch.revenue !== undefined) {
    merged.revenue = patch.revenue;
  }
  if (isEmptyField(existing?.burn) && patch.burn !== undefined) {
    merged.burn = patch.burn;
  }
  if (isEmptyField(existing?.remainingCash) && patch.remainingCash !== undefined) {
    merged.remainingCash = patch.remainingCash;
  }
  if (isEmptyField(existing?.reportSentiment) && patch.reportSentiment !== undefined) {
    merged.reportSentiment = patch.reportSentiment;
  }
  if (isEmptyField(existing?.rawText) && patch.rawText !== undefined) {
    merged.rawText = patch.rawText;
  }

  return merged;
}

async function extractFounderUpdateText(text: string): Promise<FounderUpdateExtraction> {
  const fn = httpsCallable<{ text: string }, FounderUpdateExtraction>(functions, 'extractFounderUpdate');
  const result = await fn({ text });
  return result.data;
}

function updateSortTime(update: CompanyUpdate): number {
  const received = update.receivedAt ? new Date(update.receivedAt).getTime() : NaN;
  if (Number.isFinite(received)) return received;
  const dated = new Date(update.date).getTime();
  return Number.isFinite(dated) ? dated : 0;
}

function sourceLabel(update: CompanyUpdate): string {
  if (update.source === 'email') return 'Email';
  return 'Manual';
}

type InboxReviewCardProps = {
  item: FounderUpdateInboxItem;
  companies: InvestmentCompany[];
  defaultCompanyId: string;
  userId: string;
  busy: boolean;
  onAssign: (item: FounderUpdateInboxItem, companyId: string) => Promise<void>;
  onDismiss: (item: FounderUpdateInboxItem) => Promise<void>;
};

function InboxReviewCard({
  item,
  companies,
  defaultCompanyId,
  userId,
  busy,
  onAssign,
  onDismiss,
}: InboxReviewCardProps) {
  const [selectedCompanyId, setSelectedCompanyId] = useState(defaultCompanyId);
  const [expanded, setExpanded] = useState(false);

  const companyOptions = useMemo(
    () =>
      companies.map((company) => ({
        value: company.id,
        label: company.name,
      })),
    [companies]
  );

  const summary = item.extraction?.summary?.trim();

  return (
    <li className="inboxReviewCard">
      <div className="inboxReviewCardTop">
        <div className="inboxReviewCardMeta">
          <span className="inboxReviewBadge">Needs review</span>
          <span className="inboxReviewDate">{formatDateLong(item.receivedAt)}</span>
        </div>
        <p className="inboxReviewFrom">
          <span className="inboxReviewLabel">From</span> {item.fromEmail || '—'}
        </p>
        {item.subject ? (
          <p className="inboxReviewSubject">
            <span className="inboxReviewLabel">Subject</span> {item.subject}
          </p>
        ) : null}
        {summary ? <p className="inboxReviewSummary">{summary}</p> : null}
      </div>

      <div className="inboxReviewActions">
        <GlobalDropdown
          label="Assign to company"
          value={selectedCompanyId}
          onChange={setSelectedCompanyId}
          options={companyOptions}
          disabled={busy || companyOptions.length === 0}
          required
        />
        <div className="inboxReviewButtons">
          <GlobalButton
            type="button"
            variant="primary"
            disabled={busy || !selectedCompanyId}
            onClick={() => void onAssign(item, selectedCompanyId)}
          >
            Assign
          </GlobalButton>
          <GlobalButton
            type="button"
            variant="secondary"
            destructive
            disabled={busy}
            onClick={() => void onDismiss(item)}
          >
            Dismiss
          </GlobalButton>
          <GlobalButton
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? 'Hide email' : 'View email'}
          </GlobalButton>
        </div>
      </div>

      {expanded ? <pre className="inboxReviewRaw">{item.rawText}</pre> : null}
      {!userId ? <p className="muted inboxReviewHint">Sign in to assign or dismiss.</p> : null}
    </li>
  );
}

export function CompanyUpdateTimeline({
  companyId,
  companyName,
  updates,
  companies,
  unmatchedInboxItems,
  userId,
  setUpdates,
  setMonthlyUpdates,
  onAddManualUpdate,
}: CompanyUpdateTimelineProps) {
  const [expandedUpdateIds, setExpandedUpdateIds] = useState<Record<string, boolean>>({});
  const [busyItemId, setBusyItemId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const companyUpdates = useMemo(
    () =>
      updates
        .filter((update) => update.companyId === companyId)
        .sort((a, b) => updateSortTime(b) - updateSortTime(a)),
    [updates, companyId]
  );

  async function dismissInboxItem(item: FounderUpdateInboxItem) {
    if (!userId) return;
    setActionError(null);
    setBusyItemId(item.id);
    try {
      await deleteDoc(
        doc(db, COLLECTION_FOUNDER_UPDATE_INBOX_ITEMS, ownedItemDocId(userId, item.id))
      );
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to dismiss inbox item');
    } finally {
      setBusyItemId(null);
    }
  }

  async function assignInboxItem(item: FounderUpdateInboxItem, targetCompanyId: string) {
    if (!userId || !targetCompanyId) return;
    setActionError(null);
    setBusyItemId(item.id);

    try {
      const extraction = item.extraction ?? (await extractFounderUpdateText(item.rawText));
      const now = new Date().toISOString();
      const companyUpdateId = item.id;

      const companyUpdate: CompanyUpdate = {
        id: companyUpdateId,
        companyId: targetCompanyId,
        date: item.receivedAt,
        rawText: item.rawText,
        aiSummary: extraction.summary,
        sentiment: extraction.sentiment,
        keyPoints: extraction.keyPoints,
        source: 'email',
        sourceInboxItemId: item.id,
        sourceMessageId: item.sourceMessageId,
        fromEmail: item.fromEmail,
        subject: item.subject,
        receivedAt: item.receivedAt,
        extraction,
        createdAt: now,
      };

      setUpdates((prev) => {
        const idx = prev.findIndex((entry) => entry.id === companyUpdateId);
        if (idx === -1) return [...prev, companyUpdate];
        const copy = [...prev];
        copy[idx] = { ...copy[idx], ...companyUpdate };
        return copy;
      });

      const monthlyPatch = mapExtractionToMonthlyUpdatePatch(extraction, item.rawText);
      if (monthlyPatch) {
        const monthlyId = monthlyUpdateIdForPeriod(targetCompanyId, monthlyPatch.periodMonth);
        setMonthlyUpdates((prev) => {
          const existing = prev.find((entry) => entry.id === monthlyId);
          const merged = mergeMonthlyUpdateFromExtraction(existing, monthlyPatch);
          if (existing) {
            return prev.map((entry) =>
              entry.id === monthlyId ? { ...entry, ...merged, updatedAt: now } : entry
            );
          }
          return [
            ...prev,
            {
              id: monthlyId,
              companyId: targetCompanyId,
              ...merged,
              createdAt: now,
              updatedAt: now,
            },
          ];
        });
      }

      await deleteDoc(
        doc(db, COLLECTION_FOUNDER_UPDATE_INBOX_ITEMS, ownedItemDocId(userId, item.id))
      );
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to assign inbox item');
    } finally {
      setBusyItemId(null);
    }
  }

  function toggleUpdateExpanded(id: string) {
    setExpandedUpdateIds((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <div className="companyUpdateTimeline">
      {unmatchedInboxItems.length > 0 ? (
        <section className="inboxReviewSection" aria-label="Unmatched founder emails">
          <div className="inboxReviewHead">
            <h2 className="inboxReviewTitle">Review forwarded emails</h2>
            <p className="muted inboxReviewIntro">
              {unmatchedInboxItems.length === 1
                ? 'One email could not be auto-matched.'
                : `${unmatchedInboxItems.length} emails could not be auto-matched.`}{' '}
              Assign to {companyName} or dismiss.
            </p>
          </div>
          {actionError ? <p className="form-error inboxReviewError">{actionError}</p> : null}
          <ul className="inboxReviewList">
            {unmatchedInboxItems.map((item) => (
              <InboxReviewCard
                key={item.id}
                item={item}
                companies={companies}
                defaultCompanyId={companyId}
                userId={userId}
                busy={busyItemId === item.id}
                onAssign={assignInboxItem}
                onDismiss={dismissInboxItem}
              />
            ))}
          </ul>
        </section>
      ) : null}

      <section className="companyUpdatesSection" aria-label="Company notes timeline">
        {companyUpdates.length === 0 ? (
          onAddManualUpdate ? (
            <GlobalEmptyState buttonText="Add note" onButtonClick={onAddManualUpdate} />
          ) : (
            <GlobalEmptyState />
          )
        ) : (
          <ul className="companyUpdateList">
            {companyUpdates.map((update) => {
              const expanded = Boolean(expandedUpdateIds[update.id]);
              const summary = update.aiSummary?.trim() || update.extraction?.summary?.trim();
              const displayDate = update.receivedAt || update.date;

              return (
                <li key={update.id} className="companyUpdateCard">
                  <div className="companyUpdateCardTop">
                    <button
                      type="button"
                      className="companyUpdateExpand"
                      onClick={() => toggleUpdateExpanded(update.id)}
                      aria-expanded={expanded}
                      aria-label={expanded ? 'Collapse update' : 'Expand update'}
                    >
                      <span className="companyUpdateExpandIcon" aria-hidden>
                        {expanded ? '▼' : '▶'}
                      </span>
                      <span className="companyUpdateWhen">{formatDateLong(displayDate)}</span>
                    </button>
                    <div className="companyUpdateMeta">
                      <span className="companyUpdateSource">{sourceLabel(update)}</span>
                      <span className={sentimentClassName(update.sentiment)}>{update.sentiment}</span>
                    </div>
                  </div>

                  {update.source === 'email' ? (
                    <div className="companyUpdateEmailMeta">
                      {update.fromEmail ? (
                        <p>
                          <span className="inboxReviewLabel">From</span> {update.fromEmail}
                        </p>
                      ) : null}
                      {update.subject ? (
                        <p>
                          <span className="inboxReviewLabel">Subject</span> {update.subject}
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {summary ? <p className="companyUpdateSummary">{summary}</p> : null}

                  {update.keyPoints.length > 0 ? (
                    <ul className="companyUpdateKeyPoints">
                      {update.keyPoints.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  ) : null}

                  {expanded ? <pre className="companyUpdateRaw">{update.rawText}</pre> : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
